import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import type { FC } from "react";

interface FloatingDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export const FloatingDropdown: FC<FloatingDropdownProps> = ({ 
  trigger, 
  children, 
  isOpen, 
  onToggle, 
  onClose 
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  const calculatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Get dropdown height - use a more reasonable fallback
      const dropdownHeight = dropdownRef.current?.offsetHeight || 200; // reduced fallback height
      
      setPosition({
        top: rect.top + scrollY - dropdownHeight - 8, // 8px gap above
        left: rect.left + scrollX
      });
      setIsPositioned(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false);
      // Calculate position immediately
      calculatePosition();
      
      // Recalculate after a brief delay to get accurate dropdown dimensions
      const timer1 = setTimeout(() => {
        calculatePosition();
      }, 10);
      
      // Additional recalculation to ensure accuracy after render
      const timer2 = setTimeout(() => {
        calculatePosition();
      }, 50);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      setIsPositioned(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is on Clerk elements (OrganizationSwitcher modal)
      const isClerkElement = target.closest('[data-clerk-portal]') || 
                            target.closest('.cl-organizationSwitcherPopover') ||
                            target.closest('.cl-modal') ||
                            target.closest('.cl-organizationSwitcher') ||
                            target.className?.includes('cl-');
      
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !isClerkElement
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose]);

  const dropdownContent = isOpen ? (
    <div 
      ref={dropdownRef}
      className={`fixed z-[9999] w-80 bg-blue-700 rounded-lg shadow-xl border border-white dark:border-blue-600 overflow-hidden transition-opacity duration-200 ${
        isPositioned ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {children}
    </div>
  ) : null;

  return (
    <>
      <div ref={triggerRef} onClick={onToggle}>
        {trigger}
      </div>
      
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}; 